import type { IResizableBoxHeight } from '@/features/work-areas/useResizableBoxHeight';
import { useT } from '@/i18n/LocaleProvider';

/**
 * F-0381: the expanded work-area box's bottom edge, draggable to resize the box.
 *
 * `role="separator"` with `aria-orientation="horizontal"` (AC-2) is the ARIA pattern for a
 * draggable divider that moves vertically — the line itself is horizontal, which is what the
 * orientation names, not the direction it travels in (the same pattern a resizable split-pane
 * uses). `aria-valuenow`/`aria-valuemin`/`aria-valuemax` expose the current height in pixels, so a
 * screen reader can say "216 of 120 to 540" rather than nothing.
 */
export function WorkAreaBoxResizeHandle({ resize }: { resize: IResizableBoxHeight }) {
  const t = useT();
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label={t('navigation.reorder.resizeWorkAreas')}
      aria-valuenow={Math.round(resize.height)}
      aria-valuemin={resize.minHeight}
      aria-valuemax={Math.round(resize.maxHeight)}
      aria-valuetext={t('navigation.reorder.pixelsTall', { height: Math.round(resize.height) })}
      tabIndex={0}
      title={t('navigation.reorder.resizeHint')}
      className={`group relative -mx-1 -mb-1 mt-0.5 flex h-2.5 cursor-row-resize touch-none items-center justify-center rounded-b-md outline-none focus-visible:ring-2 focus-visible:ring-focus ${
        resize.dragging ? 'bg-primary-500/10' : ''
      }`}
      onPointerDown={resize.onHandlePointerDown}
      onKeyDown={resize.onHandleKeyDown}
      onDoubleClick={resize.onHandleDoubleClick}
    >
      <span
        aria-hidden="true"
        className={`h-1 w-8 rounded-full bg-gray-300 transition-colors duration-150 group-hover:bg-gray-400 dark:bg-gray-600 dark:group-hover:bg-gray-500 ${
          resize.dragging ? '!bg-primary-500' : ''
        }`}
      />
    </div>
  );
}
